import { useDispatch } from 'react-redux';
import axios from '../utils/axiosConfig';
import { errorHandler } from '../utils/errorHandler';
import toast from 'react-hot-toast';
import {
    setResyncLoading,
    setResyncStatus,
    setResyncError,
    setLastSyncedAt,
} from '../redux/slices/integration';

// Sync status callback for external status tracking
let syncStatusCallback: ((status: string, data?: any) => void) | null = null;
let syncCompletionCallback: ((message: string, lastSyncedAt: string) => void) | null = null;

const useIntegrationService = () => {
    const dispatch = useDispatch();

    const showError = (error: any) => {
        errorHandler.showError(error);
    };

    /**
     * Initiates a resync for an epic/roadmap
     * @param id - The ID of the entity to resync (roadmap_id)
     * @param integration_type - The integration type (e.g., 'azure', 'jira')
     */
    const resyncEpic = async (id: string, integration_type: string) => {
        dispatch(setResyncLoading(true));
        try {
            const response = await axios.post('/integration/resync_epic', {
                id,
                integration_type,
            });
            return response.data;
        } catch (error) {
            showError(error);
            dispatch(setResyncError(error));
            throw error;
        } finally {
            dispatch(setResyncLoading(false));
        }
    };

    /**
     * Gets the resync status for an entity
     * @param integration_type - The integration type (e.g., 'azure', 'jira')
     * @param id - The ID of the entity
     */
    const getResyncStatus = async (integration_type: string, id: string) => {
        try {
            const response = await axios.get(`/integration/resync/status/${integration_type}/${id}`);
            return response.data;
        } catch (error) {
            showError(error);
            return null;
        }
    };

    /**
     * Initiates resync and polls for status until completion
     * @param id - The ID of the entity to resync (roadmap_id)
     * @param integration_type - The integration type (e.g., 'azure', 'jira')
     */
    const resyncEpicWithStatus = async (id: string, integration_type: string) => {
        try {
            // Start the resync process
            await resyncEpic(id, integration_type);

            // Notify that sync started
            dispatch(setResyncStatus('in_progress'));
            if (syncStatusCallback) {
                syncStatusCallback('in_progress');
            }

            toast.success('Sync started successfully', {
                style: {
                    background: 'var(--toast-bg, #ffffff)',
                    color: 'var(--toast-text, #000000)',
                    border: '1px solid var(--toast-border, #e5e7eb)',
                },
                className: 'dark:!bg-gray-800 dark:!text-white text-sm dark:!border-gray-600',
                duration: 3000,
            });

            // Poll for sync status
            const pollInterval = 10000; // Poll every 10 seconds
            const maxPollingTime = 600000; // 10 minutes max
            let pollingStartTime = Date.now();

            const pollStatus = async (): Promise<void> => {
                try {
                    const statusResponse = await getResyncStatus(integration_type, id);

                    if (statusResponse) {
                        const { sync_status, last_synced_at, message } = statusResponse;

                        // Update Redux state
                        dispatch(setResyncStatus(sync_status));

                        // Update sync status via callback
                        if (syncStatusCallback) {
                            const dataToPass = sync_status === 'completed' ? { last_synced_at } : {};
                            syncStatusCallback(sync_status, dataToPass);
                        }

                        if (sync_status === 'completed') {
                            dispatch(setLastSyncedAt(last_synced_at));
                            
                            // Use custom completion callback
                            if (syncCompletionCallback) {
                                syncCompletionCallback(message || 'Sync completed successfully!', last_synced_at);
                            }
                            return;
                        } else if (sync_status === 'failed' || sync_status === 'error') {
                            toast.error(message || 'Sync failed', {
                                style: {
                                    background: 'var(--toast-bg, #ffffff)',
                                    color: 'var(--toast-text, #000000)',
                                    border: '1px solid var(--toast-border, #e5e7eb)',
                                },
                                className: 'dark:!bg-gray-800 dark:!text-white text-sm dark:!border-gray-600',
                            });
                            dispatch(setResyncStatus('failed'));
                            if (syncStatusCallback) {
                                syncStatusCallback('failed');
                            }
                            return;
                        } else if (sync_status === 'in_progress' || sync_status === 'pending') {
                            // Continue polling
                            if (Date.now() - pollingStartTime < maxPollingTime) {
                                setTimeout(pollStatus, pollInterval);
                            } else {
                                toast.error('Sync is taking longer than expected. Please check status later.', {
                                    style: {
                                        background: 'var(--toast-bg, #ffffff)',
                                        color: 'var(--toast-text, #000000)',
                                        border: '1px solid var(--toast-border, #e5e7eb)',
                                    },
                                    className: 'dark:!bg-gray-800 dark:!text-white text-sm dark:!border-gray-600',
                                });
                                dispatch(setResyncStatus('timeout'));
                                if (syncStatusCallback) {
                                    syncStatusCallback('timeout');
                                }
                            }
                        }
                    } else {
                        // If no status response, stop polling
                        dispatch(setResyncStatus('failed'));
                        if (syncStatusCallback) {
                            syncStatusCallback('failed');
                        }
                    }
                } catch (error) {
                    console.error('Error polling resync status:', error);
                    dispatch(setResyncStatus('failed'));
                    if (syncStatusCallback) {
                        syncStatusCallback('failed');
                    }
                }
            };

            // Start polling
            setTimeout(pollStatus, pollInterval);
        } catch (error) {
            showError(error);
            dispatch(setResyncStatus('failed'));
            if (syncStatusCallback) {
                syncStatusCallback('failed');
            }
        }
    };

    // Helper functions to set callbacks
    const setSyncStatusCallback = (callback: (status: string, data?: any) => void) => {
        syncStatusCallback = callback;
    };

    const setSyncCompletionCallback = (callback: (message: string, lastSyncedAt: string) => void) => {
        syncCompletionCallback = callback;
    };

   
    return {
        resyncEpic,
        getResyncStatus,
        resyncEpicWithStatus,
        setSyncStatusCallback,
        setSyncCompletionCallback,
     
    };
};

export default useIntegrationService;
